import { Component } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Surface the failing variable / stack to the console so operators can pinpoint
        // 'cannot read property X of null' style crashes immediately.
        console.error("[ErrorBoundary]", error?.message || error);
        console.error("[ErrorBoundary] component stack:", info?.componentStack);
        this.setState({ info });
    }

    render() {
        if (!this.state.error) return this.props.children;

        const message = this.state.error?.message || String(this.state.error || "Unknown error");
        const stack = this.state.info?.componentStack || "";
        const offendingLine = stack.split("\n").map((s) => s.trim()).find(Boolean) || "";

        return (
            <div
                data-testid={this.props.testId || "error-boundary"}
                className="border border-red-500/50 bg-red-500/5 p-6 m-6"
            >
                <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                        <div className="overline text-red-400">Runtime Error Caught</div>
                        <div className="heading-display text-xl mt-2">
                            {this.props.label || "Something blew up"}
                        </div>
                        <div className="font-mono text-sm text-red-400 mt-3 break-words">
                            {message}
                        </div>
                        {offendingLine && (
                            <div className="font-mono text-[11px] text-snow-muted mt-2 truncate">
                                Source: {offendingLine}
                            </div>
                        )}
                        <button
                            onClick={() => {
                                this.setState({ error: null, info: null });
                                if (typeof window !== "undefined") window.location.reload();
                            }}
                            className="mt-4 cerb-btn-secondary inline-flex items-center gap-2"
                            data-testid="error-boundary-reload"
                        >
                            <RotateCcw className="w-3 h-3" /> Reload
                        </button>
                        {stack && (
                            <details className="mt-4">
                                <summary className="cursor-pointer text-[11px] font-mono text-snow-muted uppercase tracking-wider">
                                    Stack trace
                                </summary>
                                <pre className="mt-2 text-[10px] text-snow-muted font-mono whitespace-pre-wrap leading-relaxed border border-hairline bg-jet p-3">
                                    {stack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
